import React, { useCallback, useState } from 'react';
import { css } from '@emotion/react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { Info as InfoIcon } from '@material-ui/icons';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { colors } from '@/styles';

const CustomizableTableProfileControl = ({
    disabled = false,
    tableType,
    currentProfileName,
    currentProfileId,
    selectedProfileId,
    onSaveNew,
    onOverwrite,
    onImportById,
    onDeleteCurrent,
    saving = false,
    overwriting = false,
    deleting = false,
}) => {
    // Create Profile Modal state
    const [createProfileOpen, setCreateProfileOpen] = useState(false);
    const [name, setName] = useState('');
    const [importId, setImportId] = useState('');
    const [importError, setImportError] = useState('');
    const [importing, setImporting] = useState(false);
    const [stagedSettings, setStagedSettings] = useState(null);

    // Manage Profiles Modal state
    const [manageProfilesOpen, setManageProfilesOpen] = useState(false);
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [profileName, setProfileName] = useState('');

    const closeCreateProfile = useCallback(() => {
        setCreateProfileOpen(false);
        setName('');
        setImportId('');
        setImportError('');
        setImporting(false);
        setStagedSettings(null);
    }, []);

    const closeManageProfiles = useCallback(() => {
        setManageProfilesOpen(false);
        setProfileName('');
    }, []);

    const handleOpenManageProfiles = useCallback(() => {
        setProfileName(currentProfileName || '');
        setManageProfilesOpen(true);
    }, [currentProfileName]);

    const handleImport = useCallback(async () => {
        const trimmed = (importId || '').trim();
        if (!trimmed) return;

        setImportError('');
        setImporting(true);
        try {
            const profile = await onImportById?.(trimmed);
            if (!profile) {
                setImportError('Profile not found.');
                return;
            }
            if (tableType && profile.table_type !== tableType) {
                setImportError(`Profile is for ${profile.table_type}.`);
                return;
            }

            setStagedSettings(profile.settings || {});
            const nextName = `${profile.name || 'Imported Profile'} copy`;
            setName(nextName);
        } catch (e) {
            setImportError(e?.message || 'Import failed.');
        } finally {
            setImporting(false);
        }
    }, [importId, onImportById, tableType]);

    const handleSaveNew = useCallback(() => {
        onSaveNew?.(name, stagedSettings);
        closeCreateProfile();
    }, [name, stagedSettings, onSaveNew, closeCreateProfile]);

    const handleOverwrite = useCallback(() => {
        onOverwrite?.(profileName);
        closeManageProfiles();
    }, [onOverwrite, profileName, closeManageProfiles]);

    const resolvedProfileName = currentProfileName || 'New Unsaved';
    const resolvedProfileId = currentProfileId || '-';
    const canDelete = !!selectedProfileId && resolvedProfileId !== '-' && !disabled;

    return (
        <>
            <div
                css={css`
                    display: flex;
                    gap: 8px;
                `}
            >
                <PrimaryButton
                    onClick={() => setCreateProfileOpen(true)}
                    disabled={disabled}
                    css={css`
                        height: 36px;
                    `}
                >
                    Create Profile
                </PrimaryButton>
                <PrimaryButton
                    onClick={handleOpenManageProfiles}
                    disabled={disabled}
                    css={css`
                        height: 36px;
                    `}
                >
                    Manage Profiles
                </PrimaryButton>
            </div>

            {/* Create Profile Modal */}
            <Dialog open={createProfileOpen} onClose={closeCreateProfile} maxWidth="xs" fullWidth>
                <DialogTitle>Create Table Profile</DialogTitle>
                <DialogContent>
                    {/* Import Profile Section */}
                    <Typography
                        variant="subtitle2"
                        css={css`
                            color: #666;
                            margin-bottom: 8px;
                        `}
                    >
                        Import Profile
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Profile ID"
                        value={importId}
                        onChange={(e) => setImportId(e.target.value)}
                        disabled={importing}
                    />
                    {importError ? (
                        <Typography
                            css={css`
                                margin-top: 8px;
                                color: #d23e3e;
                                font-weight: 600;
                            `}
                        >
                            {importError}
                        </Typography>
                    ) : null}
                    <div
                        css={css`
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            margin-top: 12px;
                            margin-bottom: 16px;
                        `}
                    >
                        <PrimaryButton
                            variant="contained"
                            color="primary"
                            disabled={!importId?.trim() || importing || disabled}
                            onClick={handleImport}
                        >
                            Import
                        </PrimaryButton>
                        <Tooltip
                            title="Enter a profile ID from another user to import their table settings. The imported settings will be staged and applied when you click 'Save'. You can edit the profile name before saving."
                            arrow
                            placement="top"
                        >
                            <IconButton
                                size="small"
                                css={css`
                                    color: ${colors.greens.primary};
                                    padding: 4px;
                                `}
                            >
                                <InfoIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </div>

                    {/* Profile Name Input */}
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Profile Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        css={css`
                            margin-top: 8px;
                        `}
                    />
                </DialogContent>
                <DialogActions
                    css={css`
                        justify-content: space-between;
                        padding: 8px 24px;
                    `}
                >
                    <SecondaryButton variant="outlined" onClick={closeCreateProfile}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        variant="contained"
                        color="primary"
                        disabled={!name?.trim() || saving}
                        onClick={handleSaveNew}
                    >
                        Save
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            {/* Manage Profiles Modal */}
            <Dialog open={manageProfilesOpen} onClose={closeManageProfiles} maxWidth="xs" fullWidth>
                <DialogTitle>Manage Table Profiles</DialogTitle>
                <DialogContent>
                    <Typography
                        variant="subtitle2"
                        css={css`
                            color: #666;
                            margin-bottom: 8px;
                        `}
                    >
                        Profile Name:
                    </Typography>
                    <Typography
                        css={css`
                            margin-bottom: 8px;
                            font-weight: 600;
                        `}
                    >
                        {resolvedProfileName}
                    </Typography>
                    <Typography
                        variant="subtitle2"
                        css={css`
                            color: #666;
                            margin-bottom: 4px;
                        `}
                    >
                        Profile ID:
                    </Typography>
                    <Typography
                        css={css`
                            font-family: monospace;
                            word-break: break-all;
                            margin-bottom: 16px;
                        `}
                    >
                        {resolvedProfileId}
                    </Typography>
                    <TextField
                        fullWidth
                        variant="outlined"
                        label="Profile Name"
                        value={profileName}
                        onChange={(e) => setProfileName(e.target.value)}
                        disabled={!selectedProfileId}
                        css={css`
                            margin-bottom: 16px;
                        `}
                    />
                    <PrimaryButton
                        variant="contained"
                        color="primary"
                        disabled={!canDelete || deleting}
                        onClick={() => setConfirmDeleteOpen(true)}
                        css={css`
                            background: #d23e3e;
                            height: 36px;
                            &:hover {
                                background: #b53636;
                            }
                        `}
                    >
                        Delete Profile
                    </PrimaryButton>
                    <Divider
                        css={css`
                            margin: 16px -24px 16px -24px;
                            background: gray;
                        `}
                    />
                </DialogContent>
                <DialogActions
                    css={css`
                        justify-content: space-between;
                        padding: 8px 24px;
                    `}
                >
                    <SecondaryButton variant="outlined" onClick={closeManageProfiles}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        variant="contained"
                        color="primary"
                        disabled={!selectedProfileId || !profileName?.trim() || overwriting}
                        onClick={handleOverwrite}
                    >
                        Save
                    </PrimaryButton>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the profile "{resolvedProfileName}"? This action cannot be
                        undone.
                    </Typography>
                </DialogContent>
                <DialogActions
                    css={css`
                        justify-content: space-between;
                        padding: 8px 24px;
                    `}
                >
                    <SecondaryButton variant="outlined" onClick={() => setConfirmDeleteOpen(false)}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton
                        variant="contained"
                        color="primary"
                        disabled={deleting}
                        onClick={() => {
                            onDeleteCurrent?.();
                            setConfirmDeleteOpen(false);
                        }}
                        css={css`
                            background: #d23e3e;
                            &:hover {
                                background: #b53636;
                            }
                        `}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CustomizableTableProfileControl;
